<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import CurrenciesAPI from 'dashboard/api/currencies';

const emit = defineEmits(['updated']);

const { t } = useI18n();
const store = useStore();
const { accountId } = useAccount();

const currencies = ref([]);
const preferredCurrencyId = ref(null);
const draftCurrencyId = ref(null);
const isReady = ref(false);
const isSaving = ref(false);
const dialogRef = ref(null);

const getAccount = computed(() => store.getters['accounts/getAccount']);
const isLoading = computed(
  () => store.getters['accounts/getUIFlags'].isFetchingItem
);

const selectedCurrency = computed(() =>
  currencies.value.find(c => c.id === preferredCurrencyId.value)
);

const triggerLabel = computed(() => {
  const code = selectedCurrency.value?.code;
  if (code) {
    return t('GENERAL_SETTINGS.FORM.PREFERRED_CURRENCY.TRIGGER_WITH_CODE', {
      code,
    });
  }
  return t('GENERAL_SETTINGS.FORM.PREFERRED_CURRENCY.LABEL');
});

const syncFromAccount = async () => {
  const account = getAccount.value(accountId.value);
  preferredCurrencyId.value = account?.preferred_currency_id || null;
  draftCurrencyId.value = preferredCurrencyId.value;
  await nextTick();
};

const loadCurrencies = async () => {
  try {
    const response = await CurrenciesAPI.list();
    currencies.value = response.data || [];
  } catch {
    currencies.value = [];
  }
};

const openModal = async () => {
  draftCurrencyId.value = preferredCurrencyId.value;
  await nextTick();
  dialogRef.value?.open();
};

const closeModal = () => {
  draftCurrencyId.value = preferredCurrencyId.value;
};

const savePreferredCurrency = async () => {
  if (isSaving.value) return;
  if (draftCurrencyId.value === preferredCurrencyId.value) {
    dialogRef.value?.close();
    return;
  }

  isSaving.value = true;
  try {
    await store.dispatch('accounts/update', {
      preferred_currency_id: draftCurrencyId.value || null,
    });
    await syncFromAccount();
    useAlert(t('GENERAL_SETTINGS.UPDATE.SUCCESS'));
    emit('updated');
    dialogRef.value?.close();
  } catch {
    useAlert(t('GENERAL_SETTINGS.UPDATE.ERROR'));
    await syncFromAccount();
  } finally {
    isSaving.value = false;
  }
};

watch(
  () => getAccount.value(accountId.value)?.preferred_currency_id,
  () => {
    if (!isReady.value || isSaving.value) return;
    syncFromAccount();
  }
);

onMounted(async () => {
  await loadCurrencies();
  await syncFromAccount();
  isReady.value = true;
});
</script>

<template>
  <Button
    variant="faded"
    color="slate"
    size="sm"
    icon="i-lucide-chevron-down"
    trailing-icon
    :label="triggerLabel"
    :disabled="!isReady || isSaving || isLoading"
    @click="openModal"
  />

  <Dialog
    ref="dialogRef"
    type="edit"
    :title="t('GENERAL_SETTINGS.FORM.PREFERRED_CURRENCY.LABEL')"
    :description="
      t('GENERAL_SETTINGS.FORM.PREFERRED_CURRENCY.MODAL_DESCRIPTION')
    "
    :confirm-button-label="t('GENERAL_SETTINGS.FORM.PREFERRED_CURRENCY.SAVE')"
    :is-loading="isSaving"
    width="md"
    @confirm="savePreferredCurrency"
    @close="closeModal"
  >
    <label
      for="preferred-currency-modal"
      class="mb-1.5 block text-sm font-medium text-n-slate-12"
    >
      {{ t('GENERAL_SETTINGS.FORM.PREFERRED_CURRENCY.LABEL') }}
    </label>
    <select
      id="preferred-currency-modal"
      v-model="draftCurrencyId"
      class="w-full rounded-lg border border-solid border-n-weak bg-n-background px-3 py-2 text-sm text-n-slate-12"
      :disabled="isSaving || isLoading"
    >
      <option :value="null">
        {{ t('GENERAL_SETTINGS.FORM.PREFERRED_CURRENCY.PLACEHOLDER') }}
      </option>
      <option
        v-for="currency in currencies"
        :key="currency.id"
        :value="currency.id"
      >
        {{
          t('GENERAL_SETTINGS.FORM.PREFERRED_CURRENCY.OPTION', {
            code: currency.code,
            name: currency.name,
          })
        }}
      </option>
    </select>
  </Dialog>
</template>
